import {Action} from '@ngrx/store';
import * as _ from 'lodash';
import {DEFAULT_PAGE_SIZE} from '../../shared/consts';
import {
  ChangeOrdersPageSizeAction,
  LoadOrdersSuccessAction,
  orderListActionTypes,
  OrderOrdersByAction,
  PageOrdersToAction,
  QuickSearchOrdersAction,
  SearchOrdersAction,
  ToggleOrdersSelectionAction,
  ToggleSearchOrdersAction
} from '../actions/order-list';
import {OrderFilter} from '../models/order-filter';
import {orderStatusActionTypes, OrderStatusSuccessAction} from '../actions/order-status';
import {OrderItem} from '../models/order-item';
import {closeOrdersActionTypes, CloseOrdersSuccessAction} from '../actions/close-order';
import {cancelOrdersActionTypes, CancelOrdersSuccessAction} from '../actions/cancel-order';
import {SelectedOrder} from '../models/order';

export interface OrdersListState {
  showFilter: boolean;
  quickSearch: string;
  filter: OrderFilter | string | number;
  page: number;
  pageSize: number;
  orderBy: string;
  array: OrderItem[];
  count: number;
  selectedOrder: SelectedOrder[];
}

const initialState: OrdersListState = {
  showFilter: false,
  quickSearch: undefined,
  filter: undefined,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  orderBy: 'updated descending',
  array: [],
  count: 0,
  selectedOrder: [],
};

export function OrdersListReducer(state: OrdersListState = initialState, action: Action): OrdersListState {
  switch (action.type) {
    case orderListActionTypes.TOGGLE_SEARCH:
      return handleToggleSearchAction(state, action);
    case orderListActionTypes.QUICK_SEARCH:
      return handleQuickSearchAction(state, action);
    case orderListActionTypes.SEARCH:
      return handleSearchAction(state, action);
    case orderListActionTypes.PAGE_TO:
      return handlePageToAction(state, action);
    case orderListActionTypes.CHANGE_PAGE_SIZE:
      return handleChangePageSizeAction(state, action);
    case orderListActionTypes.ORDER_BY:
      return handleOrderByAction(state, action);
    case orderListActionTypes.LOAD_SUCCESS:
      return handleLoadSuccessAction(state, action);
    case orderListActionTypes.LOAD_FAIL:
      return handleLoadErrorActions(state);
    case orderListActionTypes.SELECT_PAGE:
      return handleSelectPageAction(state);
    case orderListActionTypes.UNSELECT_PAGE:
      return handleUnselectPageAction(state);
    case orderListActionTypes.TOGGLE_SELECTION:
      return handleToggleOrdersSelectionAction(state, action);
    case orderListActionTypes.CLEAR:
      return handleClearAction(state);
    case orderListActionTypes.CLEAR_ALL:
      return handleClearAllAction();
    case orderStatusActionTypes.ADD_SUCCESS:
      return handleOrderStatusAction(state, action);
    case cancelOrdersActionTypes.CANCEL_SUCCESS:
      return handleOrderCancelAction(state, action);
    case closeOrdersActionTypes.ADD_SUCCESS:
      return handleOrderCloseStatusAction(state, action);
    default:
      return state;
  }
}

function handleClearAction(state: OrdersListState): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    filter: state.filter,
    quickSearch: state.quickSearch,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleSelectPageAction(state: OrdersListState): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    filter: state.filter,
    quickSearch: state.quickSearch,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: _.union(state.selectedOrder,  _.map(state.array, orders => ({
      uuid: orders.uuid , status: orders.status , type: orders.type ,
      validationTicketCreated: orders.validationTicketCreated, sfAlipID: orders.sfAlipID

    }))),
  };
  return newState;
}

function handleUnselectPageAction(state: OrdersListState): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    filter: state.filter,
    quickSearch: state.quickSearch,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleToggleSearchAction(state: OrdersListState, action: ToggleSearchOrdersAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: action.payload,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleQuickSearchAction(state: OrdersListState, action: QuickSearchOrdersAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: action.payload,
    filter: state.filter,
    page: 1,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleSearchAction(state: OrdersListState, action: SearchOrdersAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: action.payload,
    page: 1,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handlePageToAction(state: OrdersListState, action: PageOrdersToAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: action.payload,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleChangePageSizeAction(state: OrdersListState, action: ChangeOrdersPageSizeAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: 1,
    pageSize: action.payload,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleOrderByAction(state: OrdersListState, action: OrderOrdersByAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: action.payload,
    array: state.array,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleLoadSuccessAction(state: OrdersListState, action: LoadOrdersSuccessAction): OrdersListState {
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: action.payload.array,
    count: action.payload.count,
    selectedOrder: []
  };
  return newState;
}
function handleClearAllAction(): OrdersListState {
   return initialState;
}

function handleLoadErrorActions(state: OrdersListState): OrdersListState {
  return state;
}

function handleToggleOrdersSelectionAction(state: OrdersListState, action: ToggleOrdersSelectionAction): OrdersListState {
  const selectedIndex = _.findIndex(state.selectedOrder, ['uuid', action.payload.uuid]);
  let selected: SelectedOrder[];
  if (selectedIndex > -1) {
    selected = _.filter(state.selectedOrder, sc => sc.uuid !== action.payload.uuid);
  } else {
    const order = _.find(state.array, u => u.uuid === action.payload.uuid);
    const selectedOrder: SelectedOrder = {uuid: order.uuid, status: order.status ,
                                          type : order.type ,
                                          validationTicketCreated: order.validationTicketCreated ,
                                          sfAlipID: order.sfAlipID
    };
    selected = [...state.selectedOrder, selectedOrder];
  }

  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: state.array,
    count: state.count,
    selectedOrder: selected
  };
  return newState;
}

function handleOrderStatusAction(state: OrdersListState, action: OrderStatusSuccessAction): OrdersListState {
  const mapped = _.map(state.array, order =>
    order.uuid === action.payload.uuid ? {
    ...order, ...{status: action.payload.status} ,
    ...{setupProcessETA: action.payload.setupProcessETA},
    ...{setupProcessStatus: action.payload.setupProcessStatus},
    ...{testProcessETA: action.payload.testProcessETA} ,
    ...{syncStatus: action.payload.syncStatus} ,
    ...{testProcessStatus: action.payload.testProcessStatus}
      } : order
    );
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: mapped,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

/**
 * Iterate over state array orderItem's
 * to find correspondence with the list of order delivered by storever
 * and update theses orderItems with status 6 (delivered)
 * @param state
 * @param action
 */
function handleOrderCloseStatusAction(state: OrdersListState, action: CloseOrdersSuccessAction): OrdersListState {
  const mapped = _.map(state.array, orderItem =>
    orderItem.uuid === action.payload.find(uuid => orderItem.uuid === uuid) ? { ...orderItem, ...{status: '6'}} : orderItem
  );
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: mapped,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

function handleOrderCancelAction(state: OrdersListState, action: CancelOrdersSuccessAction): OrdersListState {
  const mapped = _.filter(state.array, order =>  order.uuid !== action.payload[0] ); // TODO: adapt for a list of delete element
  const newState: OrdersListState = {
    showFilter: state.showFilter,
    quickSearch: state.quickSearch,
    filter: state.filter,
    page: state.page,
    pageSize: state.pageSize,
    orderBy: state.orderBy,
    array: mapped,
    count: state.count,
    selectedOrder: []
  };
  return newState;
}

export const ordersListSelectors = {
  showFilter: (state: OrdersListState) => _.get(state, 'showFilter', false),
  quickSearch: (state: OrdersListState) => _.get(state, 'quickSearch'),
  filter: (state: OrdersListState) => _.get(state, 'filter'),
  page: (state: OrdersListState) => _.get(state, 'page', 1),
  pageSize: (state: OrdersListState) => _.get(state, 'pageSize', DEFAULT_PAGE_SIZE),
  orderBy: (state: OrdersListState) => _.get(state, 'orderBy'),
  array: (state: OrdersListState) => _.get(state, 'array', []),
  count: (state: OrdersListState) => _.get(state, 'count', 0),
  selectedOrder: (state: OrdersListState) => _.get(state, 'selectedOrder', []),
  selectOrderByUuid: (state: OrdersListState, props: string) => {
    return state.array.find((order: OrderItem) => order.uuid === props);
  }
};
